import React from "react"

import { DateTime, Unit } from "@eonasdan/tempus-dominus"
import { pull, orderBy } from "lodash"
import { getData } from "country-list"

import { WEEK_DAY_NUMBERS } from "modules/datepicker/constants"

export const getBookingDisabledDates = (trip, booked_date) => {
  const today = global.dateTime().startOf("date")
  const restrictions = {}
  if (!trip) return restrictions

  const nextDay = today.clone.manipulate(1, "date")
  const startDate = global.dateTime(trip.start_date)
  const endDate = global.dateTime(trip.end_date)
  const todayPlusOneYear = today.clone.manipulate(1, "year")

  restrictions.minDate = nextDay
  if (trip.seasonal && DateTime.isValid(startDate) && startDate.isAfter(nextDay)) restrictions.minDate = startDate
  if (trip.seasonal && DateTime.isValid(endDate))
    restrictions.maxDate = restrictions?.minDate?.isAfter(endDate) ? restrictions.minDate : endDate
  if (!restrictions.maxDate || todayPlusOneYear.isBefore(restrictions.maxDate)) restrictions.maxDate = todayPlusOneYear

  if (trip.booking_dates?.length) restrictions.disabledDates = trip.booking_dates
  if (trip.unavailable_dates?.length) restrictions.disabledDates = trip.unavailable_dates.concat(restrictions.disabledDates || [])
  if (restrictions.disabledDates?.length)
    restrictions.disabledDates = pull([...new Set(restrictions.disabledDates)], booked_date).map((d) => new DateTime(d + "T12:00:00"))

  restrictions.daysOfWeekDisabled = trip.unavailable_weekdays?.map((day) => WEEK_DAY_NUMBERS[day])

  return restrictions
}

export const bookingDateIsDisabled = (day, trip) => {
  const restrictions = getBookingDisabledDates(trip)
  if (restrictions?.minDate?.isAfter(day)) return true
  if (restrictions?.maxDate?.isBefore(day)) return true
  if (restrictions.daysOfWeekDisabled?.length && restrictions.daysOfWeekDisabled.includes(day.weekDay)) return true
  return restrictions.disabledDates?.length && restrictions.disabledDates.some((disabledDate) => disabledDate.isSame(day, Unit.date))
}

export const bookingDateStrIsDisabled = (day, trip) => {
  bookingDateIsDisabled(new DateTime(day + "T12:00:00"), trip)
}

export const generateGuestCountOptions = (selected = 0, max = 100) => [
  <option key={0} value={0}>
    0
  </option>,
  ...new Array(Math.max(0, max - selected)).fill().map((_, i) => (
    <option key={i + 1} value={i + 1}>
      {i + 1}
    </option>
  ))
]

export const generateCountryOptions = () => {
  const countryList = orderBy(getData(), "name")
  return [
    <option key="select" value="">
      Select country
    </option>,
    countryList.map(({ code, name }) => (
      <option key={code} value={code}>
        {name}
      </option>
    ))
  ]
}

export const brandImage = (brand) => {
  if (!brand) return "";
  const map = {
    visa: "https://img.icons8.com/color/48/000000/visa.png",
    mastercard: "https://img.icons8.com/color/48/000000/mastercard-logo.png",
    amex: "https://img.icons8.com/color/48/000000/amex.png",
    discover: "https://img.icons8.com/color/48/000000/discover.png",
    diners: "https://img.icons8.com/color/48/000000/diners-club.png",
    jcb: "https://img.icons8.com/color/48/000000/jcb.png"
  };

  return map[brand.toLowerCase()] || "https://img.icons8.com/color/48/000000/bank-card-back-side.png";
};


export const calcPayToday = (po, total, deposit_percentage = 0) => {
  if (po === "full_amount_today") return total
  if (["deposit_and_balance_later", "deposit_and_balance_in_cash"].includes(po)) return total * (deposit_percentage / 100)
  if (po === "full_amount_in_cash_on_day") return 0
  return null
}

export const calculateBookingTotal = (trip, guests = 0, bookingFeePercentage = 0) => {
  if (!trip) return

  const {
    pricing_type = "charge_per_person",
    price_per_person,
    flat_rate,
    minimum_rate,
    minimum_rate_person_count,
    rate_per_additional_person
  } = trip || {}

  const guestsDiff = guests - +minimum_rate_person_count
  const guestsGreater = guestsDiff > 0
  const types = {
    charge_per_person: +price_per_person * guests,
    charge_flat_rate: +flat_rate,
    set_minimum_rate: +minimum_rate + (guestsGreater ? guestsDiff * +rate_per_additional_person : 0)
  }

  const subtotal = types[pricing_type]
  const taxesAndFees = subtotal * (bookingFeePercentage / 100)
  const total = subtotal + taxesAndFees

  return {
    subtotal,
    taxesAndFees,
    total
  }
}
export const calculateAddOnsTotal = (selections = {}, addOns = []) => {
  if (!selections || !addOns?.length) return 0
  const byId = new Map(addOns.map((a) => [a.id, a]))
  return Object.entries(selections).reduce((sum, [id, qty]) => {
    const addOn = byId.get(Number(id)) || byId.get(id)
    if (!addOn || addOn.enabled === false) return sum
    return sum + Number(addOn.price || 0) * Number(qty || 0)
  }, 0)
}

/**
 * Summarizes how the selected add-ons participate in the booking's taxes & fees, so callers can
 * apply the platform fee (commission) and custom percentage fees on the correct per-add-on base.
 * @param {Object} selections - Add-on selections map ({ [addOnId]: qty })
 * @param {Array} addOns - Experience add-ons catalog. Each entry: { id, price, enabled,
 *   apply_custom_tax_and_fees, add_on_category: { commission_applicable, commission_percentage } }
 * @returns {{ addOnsTotal: number, customTaxableAddOnsTotal: number,
 *   commissionAddOns: Array<{ amount: number, commissionPercentage: number|null }> }}
 *   - addOnsTotal: Σ price×qty of enabled selected add-ons
 *   - customTaxableAddOnsTotal: Σ line totals of add-ons with apply_custom_tax_and_fees === true
 *   - commissionAddOns: line totals of add-ons whose category is commission_applicable, each with
 *       its category commission_percentage (null → use the standard platform rate)
 */
export const summarizeAddOnFees = (selections = {}, addOns = []) => {
  const empty = { addOnsTotal: 0, customTaxableAddOnsTotal: 0, commissionAddOns: [] }
  if (!selections || !addOns?.length) return empty
  const byId = new Map(addOns.map((a) => [a.id, a]))
  return Object.entries(selections).reduce((acc, [id, qty]) => {
    const addOn = byId.get(Number(id)) || byId.get(id)
    if (!addOn || addOn.enabled === false) return acc
    const lineTotal = Number(addOn.price || 0) * Number(qty || 0)
    if (lineTotal <= 0) return acc
    acc.addOnsTotal += lineTotal
    if (addOn.apply_custom_tax_and_fees) acc.customTaxableAddOnsTotal += lineTotal
    if (addOn.add_on_category?.commission_applicable) {
      const pct = addOn.add_on_category?.commission_percentage
      acc.commissionAddOns.push({ amount: lineTotal, commissionPercentage: pct == null ? null : Number(pct) })
    }
    return acc
  }, { addOnsTotal: 0, customTaxableAddOnsTotal: 0, commissionAddOns: [] })
}

export const buildAddOnSelectionsAttributes = (selections = {}, existingBookingAddOns = []) => {
  const activeSelections = Object.entries(selections).reduce((acc, [id, qty]) => {
    if (Number(qty) > 0) acc[Number(id) || id] = Number(qty)
    return acc
  }, {})
  const existingByAddOnId = new Map((existingBookingAddOns || []).map((row) => [Number(row.add_on_id), row]))

  const attrs = []

  for (const row of existingBookingAddOns || []) {
    if (!activeSelections[Number(row.add_on_id)]) {
      attrs.push({ id: row.id, _destroy: true })
    }
  }

  for (const [addOnId, qty] of Object.entries(activeSelections)) {
    const existing = existingByAddOnId.get(Number(addOnId))
    if (existing) {
      attrs.push({ id: existing.id, quantity: qty })
    } else {
      attrs.push({ add_on_id: Number(addOnId) || addOnId, quantity: qty })
    }
  }

  return attrs
}

export const calculateChanges = (form, booking) => {
  const { trip, booked_amount, current_amount, booking_fee_percentage = 0 } = booking || {}
  const {
    pricing_type,
    max_guests,
    price_per_person,
    flat_rate,
    minimum_rate,
    minimum_rate_person_count,
    rate_per_additional_person,
    guide_booking_fee_percentage
  } = trip || {}
  const calculation = {}

  calculation.pricingType = pricing_type

  calculation.pricePerPerson =
    (pricing_type === "set_minimum_rate" && +rate_per_additional_person) || (pricing_type === "charge_per_person" && +price_per_person) || 0
  calculation.minimumPersons =
    (pricing_type === "set_minimum_rate" && +minimum_rate_person_count) || (pricing_type === "charge_flat_rate" && +max_guests) || 0
  calculation.minimumRate = (pricing_type === "set_minimum_rate" && +minimum_rate) || 0
  calculation.flatRate = (pricing_type === "charge_flat_rate" && +flat_rate) || 0

  calculation.bookedAmount = booked_amount
  calculation.currentAmount = current_amount
  calculation.amountDiff = booked_amount - current_amount

  if (!form) return calculation

  calculation.guests = Math.max(+form?.child_count + +form?.adult_count, calculation.minimumPersons)
  const bookingTotal = calculateBookingTotal(trip, calculation.guests, booking_fee_percentage || guide_booking_fee_percentage)

  calculation.addOnsTotal = calculateAddOnsTotal(form?.add_ons, trip?.add_ons)
  // The booking fee applies to the guest subtotal always; on add-ons it applies only when the
  // add-on's category is commission_applicable (at its commission_percentage, or the booking-fee
  // percentage when null).
  const feePercent = booking_fee_percentage || guide_booking_fee_percentage || 0
  const { commissionAddOns } = summarizeAddOnFees(form?.add_ons, trip?.add_ons)
  const addOnBookingFee = commissionAddOns.reduce((sum, addOn) => {
    const rate = addOn.commissionPercentage != null ? addOn.commissionPercentage : feePercent
    return sum + (addOn.amount * rate) / 100
  }, 0)
  calculation.taxesAndFees = (bookingTotal.subtotal * feePercent) / 100 + addOnBookingFee
  calculation.totalPrice = bookingTotal.subtotal + calculation.addOnsTotal + calculation.taxesAndFees - calculation.amountDiff
  calculation.subtotal = bookingTotal.subtotal

  calculation.adultOverCount = Math.max(+form?.adult_count, calculation.minimumPersons) - calculation.minimumPersons
  calculation.childOverCount = Math.min(
    Math.max(+form?.adult_count + +form?.child_count, calculation.minimumPersons) - calculation.minimumPersons,
    +form?.child_count
  )

  if (!booking || !form) return calculation

  calculation.adultCountDiff = calculation.minimumPersons ? calculation.adultOverCount : +form.adult_count - +booking?.adult_count
  calculation.childCountDiff = calculation.minimumPersons ? calculation.childOverCount : +form.child_count - +booking?.child_count

  calculation.prevGuests = Math.max(+booking.child_count + +booking.adult_count, calculation.minimumPersons)
  const prevBookingTotal = calculateBookingTotal(trip, calculation.prevGuests, booking_fee_percentage || guide_booking_fee_percentage)
  calculation.prevTotalPrice = prevBookingTotal.total - calculation.amountDiff

  calculation.guestsDiff = calculation.guests - calculation.prevGuests
  calculation.adultPriceDiff = calculation.adultCountDiff * calculation.pricePerPerson
  calculation.childPriceDiff = calculation.childCountDiff * calculation.pricePerPerson
  calculation.totalPriceDiff = calculation.totalPrice - calculation.prevTotalPrice

  calculation.isDateChanged = form.booked_date !== booking.booked_date
  calculation.isGuestsChanged = calculation.guestsDiff !== 0

  return calculation
}
